/*
 * The reply box's files on screen (docs/2026-10-19-inbox-reply-attachments-implementation-plan.md,
 * section 4): Attach beside Send, the files under the words, and files dropped on or pasted into the
 * open box. Shown only where files can go (`use-reply-files.ts`); a box that only copies has none of
 * this. A file names itself, its size and how to take it off; one that did not go up says why and
 * what to do, at the file. Nothing here is gold: Send stays the one gold control.
 */
import type React from "react";
import { useId, useRef, useState } from "react";
import { replyFileSizeLabel } from "../../src/reply-attachment-policy.ts";
import { AttachmentIcon, CloseIcon } from "../icons.tsx";
import type { FieldCapability } from "./compose-capabilities.ts";
import type { ReplyFiles } from "./use-reply-files.ts";

/** Attach: the native picker, several files at once. */
export function AttachButton(props: { files: ReplyFiles; disabled: boolean }) {
  const picker = useRef<HTMLInputElement>(null);
  if (!props.files.available) return null;
  return (
    <>
      <input
        ref={picker}
        type="file"
        multiple
        hidden
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const chosen = [...(event.currentTarget.files ?? [])];
          // Cleared at once, so choosing the same file again is a change the box hears.
          event.currentTarget.value = "";
          props.files.add(chosen);
        }}
      />
      <button
        type="button"
        className="lcr-ghost lcr-i2-attach"
        disabled={props.disabled}
        onClick={() => picker.current?.click()}
      >
        <AttachmentIcon size={16} />
        {/* A phone keeps the clip alone, so Attach, the format and Send share one row. */}
        <span className="lcr-i2-attach-word">Attach</span>
      </button>
    </>
  );
}

/** A long name keeps its extension in view: the stem gives way, the ".pdf" stays. */
function FileName(props: { name: string }) {
  const dot = props.name.lastIndexOf(".");
  const extension = dot > 0 && props.name.length - dot <= 8 ? props.name.slice(dot) : "";
  return (
    <>
      <span className="lcr-i2-reply-file-stem">
        {extension ? props.name.slice(0, dot) : props.name}
      </span>
      {extension && <span className="lcr-i2-reply-file-extension">{extension}</span>}
    </>
  );
}

/** What the list says to a screen reader as it changes. */
function listStatus(files: ReplyFiles): string {
  const uploading = files.pending.filter((file) => file.state === "uploading").length;
  const failed = files.pending.length - uploading;
  const parts = [
    files.files.length === 1 ? "1 file attached" : `${files.files.length} files attached`,
  ];
  if (uploading) parts.push(`${uploading} uploading`);
  if (failed) parts.push(failed === 1 ? "1 did not upload" : `${failed} did not upload`);
  return parts.join(", ");
}

/**
 * The files under the words: ready ones with their size, a download and Remove; uploading ones with
 * a way to stop them; refused ones with the reason, Try again where it can work, and Remove. Frozen
 * (a send held or under way), the list shows what goes and offers no change.
 */
export function ReplyFileList(props: { files: ReplyFiles; frozen: boolean }) {
  const { files, frozen } = props;
  if (files.files.length === 0 && files.pending.length === 0) return null;
  const total = files.files.reduce((sum, file) => sum + file.bytes, 0);
  return (
    <div className="lcr-i2-reply-files">
      <ul aria-label="Files on this reply">
        {files.files.map((file) => (
          <li key={file.id} className="lcr-i2-reply-file">
            <span className="lcr-i2-reply-file-row">
              <AttachmentIcon size={14} />
              <a
                className="lcr-i2-reply-file-name"
                href={files.href(file.id)}
                download={file.fileName}
                title={file.fileName}
                aria-label={`Download ${file.fileName}`}
              >
                <FileName name={file.fileName} />
              </a>
              <span className="lcr-i2-reply-file-size">{replyFileSizeLabel(file.bytes)}</span>
              {!frozen && (
                <button
                  type="button"
                  className="lcr-icon-button lcr-i2-reply-file-remove"
                  aria-label={`Remove ${file.fileName}`}
                  title="Remove"
                  disabled={files.removing === file.id}
                  onClick={() => files.remove(file.id)}
                >
                  <CloseIcon size={14} />
                </button>
              )}
            </span>
            {files.removeFailed === file.id && (
              <p className="lcr-i2-reply-file-problem" role="alert">
                It was not removed. Try again.
              </p>
            )}
          </li>
        ))}
        {files.pending.map((file) => (
          <li key={file.uploadId} className="lcr-i2-reply-file" data-state={file.state}>
            <span className="lcr-i2-reply-file-row">
              <AttachmentIcon size={14} />
              <span className="lcr-i2-reply-file-name" title={file.name}>
                <FileName name={file.name} />
              </span>
              <span className="lcr-i2-reply-file-size">
                {file.state === "uploading"
                  ? "Uploading"
                  : file.bytes > 0
                    ? replyFileSizeLabel(file.bytes)
                    : ""}
              </span>
              {file.state === "failed" && file.retryable && !frozen && (
                <button
                  type="button"
                  className="lcr-linklike lcr-i2-reply-file-retry"
                  onClick={() => files.retry(file.uploadId)}
                >
                  Try again
                </button>
              )}
              {!frozen && (
                <button
                  type="button"
                  className="lcr-icon-button lcr-i2-reply-file-remove"
                  aria-label={
                    file.state === "uploading"
                      ? `Stop uploading ${file.name}`
                      : `Remove ${file.name}`
                  }
                  title={file.state === "uploading" ? "Stop" : "Remove"}
                  onClick={() => files.dismiss(file.uploadId)}
                >
                  <CloseIcon size={14} />
                </button>
              )}
            </span>
            {file.problem && <p className="lcr-i2-reply-file-problem">{file.problem}</p>}
          </li>
        ))}
      </ul>
      {files.files.length > 1 && files.limits && (
        <p className="lcr-i2-reply-files-total">
          {files.files.length} files · {replyFileSizeLabel(total)} of{" "}
          {replyFileSizeLabel(files.limits.combinedBytes)}
        </p>
      )}
      <p className="lcr-visually-hidden" role="status">
        {listStatus(files)}
      </p>
    </div>
  );
}

/**
 * The forwarded email's own files, which the route adds by itself (`compose-capabilities.ts`): drawn
 * as the rep's files are, Remove included, but fixed. Remove is focusable and removes nothing;
 * hovering or focusing it says why, as a fixed field does (`FieldRow`, `composer.tsx`), and pressing
 * it keeps the reason in view, since a tap on a phone does not always focus a button.
 */
export function OriginalFileList(props: { names: readonly string[]; capability: FieldCapability }) {
  const note = useId();
  const [told, setTold] = useState(false);
  const { capability } = props;
  if (props.names.length === 0 || capability.editable) return null;
  return (
    <div className="lcr-i2-reply-files lcr-i2-original-files" data-told={told || undefined}>
      <ul aria-label="Files from the original email">
        {props.names.map((name, index) => (
          // The same name can come twice; the list never changes while it is open.
          // biome-ignore lint/suspicious/noArrayIndexKey: a fixed list, drawn once per composition
          <li key={`${index}:${name}`} className="lcr-i2-reply-file" data-locked>
            <span className="lcr-i2-reply-file-row">
              <AttachmentIcon size={14} />
              <span className="lcr-i2-reply-file-name" title={name}>
                <FileName name={name} />
              </span>
              <button
                type="button"
                className="lcr-icon-button lcr-i2-reply-file-remove"
                aria-label={`Remove ${name}`}
                aria-disabled="true"
                aria-describedby={note}
                onClick={() => setTold(true)}
              >
                <CloseIcon size={14} />
              </button>
            </span>
          </li>
        ))}
      </ul>
      <p className="lcr-i2-field-why lcr-i2-original-why" id={note} role="note">
        {capability.reason}
      </p>
    </div>
  );
}

/** Files and folders a drop carries; a folder is named so the box can say it cannot be attached. */
function dropped(data: DataTransfer): { files: File[]; folders: string[] } {
  const items = [...data.items].filter((item) => item.kind === "file");
  if (!items.every((item) => typeof item.webkitGetAsEntry === "function"))
    return { files: [...data.files], folders: [] };
  const files: File[] = [];
  const folders: string[] = [];
  for (const item of items) {
    const entry = item.webkitGetAsEntry();
    const file = item.getAsFile();
    if (entry?.isDirectory) folders.push(entry.name);
    else if (file) files.push(file);
  }
  return { files, folders };
}

const carriesFiles = (data: DataTransfer | null): data is DataTransfer =>
  data !== null && [...data.types].includes("Files");

/**
 * Files dropped on the open box, or pasted into it, become its files. Only file objects are taken:
 * dragged or pasted words and links go to the editor as before, and nothing becomes an image inside
 * the text. Off where files cannot go or the box is frozen.
 */
export function useFileDrop(files: ReplyFiles, frozen: boolean) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const active = files.available && !frozen;
  const handlers: Pick<
    React.HTMLAttributes<HTMLElement>,
    "onDragEnter" | "onDragOver" | "onDragLeave" | "onDropCapture" | "onPasteCapture"
  > = active
    ? {
        onDragEnter: (event) => {
          if (!carriesFiles(event.dataTransfer)) return;
          event.preventDefault();
          depth.current += 1;
          setOver(true);
        },
        onDragOver: (event) => {
          if (!carriesFiles(event.dataTransfer)) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = "copy";
        },
        onDragLeave: (event) => {
          if (!carriesFiles(event.dataTransfer)) return;
          depth.current = Math.max(0, depth.current - 1);
          if (depth.current === 0) setOver(false);
        },
        onDropCapture: (event) => {
          if (!carriesFiles(event.dataTransfer)) return;
          // Taken here, before the editor sees it: the browser does not open the file, and the
          // editor does not draw it into the words.
          event.preventDefault();
          event.stopPropagation();
          depth.current = 0;
          setOver(false);
          const { files: chosen, folders } = dropped(event.dataTransfer);
          files.add(chosen, folders);
        },
        onPasteCapture: (event) => {
          const data = event.clipboardData;
          if (data.files.length === 0) return;
          const types = [...data.types];
          // Copied words that bring a picture along are words: the editor takes them.
          if (types.includes("text/plain") || types.includes("text/html")) return;
          event.preventDefault();
          event.stopPropagation();
          files.add([...data.files]);
        },
      }
    : {};
  // Read as an attribute: present while files are over the box, absent otherwise.
  return { dropping: (active && over) || undefined, handlers };
}
